import Icons from '../../../Icons'
import { lightTheme } from '@/customTheme';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyletron } from 'styletron-react'
import AnimatedPlaceholder from './AnimatedPlaceholder';
import shourcutsManager from '@/scenes/engine/utils/shourcutsManager';

interface DynamicTextareaProps {
  prompt: string;
  setPrompt: (value: string) => void;
  wrapScrollRef: React.RefObject<any>;
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onUndoRedoShortcut?: () => void;
  placeholder: string | string[];
  disabled?: boolean;
  autoFocus?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  showClearTextButton?: boolean;
  textareaId?: string;
  padding?: number[];
  scrollbarTrackMarginTop?: number;
}

const DynamicTextarea = ({
  prompt,
  setPrompt,
  wrapScrollRef,
  isCollapsed,
  setIsCollapsed,
  handleKeyDown,
  onUndoRedoShortcut,
  placeholder,
  disabled,
  autoFocus = true,
  onFocus,
  onBlur,
  showClearTextButton,
  textareaId,
  padding,
  scrollbarTrackMarginTop = 0,
}: DynamicTextareaProps) => {
  const { t } = useTranslation()
  const [css] = useStyletron()
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false)

  const handleChange = useCallback((e) => {
    setPrompt(e.target.value);
  }, [setPrompt]);

  useEffect(() => {
    if (!prompt) return
  }, [prompt])

  // Focus only when this instance expands. `preventScroll` because mounting mid
  // slide-in leaves the box translated past its container's edge, and the default
  // scroll-into-view then jolts the surrounding UI up and back down.
  useEffect(() => {
    if (!isCollapsed && !disabled) {
      autoFocus && textareaRef.current?.focus({ preventScroll: true })
    }
  }, [isCollapsed, disabled, autoFocus])

  useEffect(() => {
    if (disabled) {
      textareaRef.current.value = '';
      handleChange({ target: textareaRef.current })
      return
    }
    if(isCollapsed) {
      handleChange({ target: textareaRef.current })
      textareaRef.current.blur()
      return
    } else {
      autoFocus && textareaRef.current.focus({ preventScroll: true })
    }
  }, [isCollapsed, disabled, autoFocus, setPrompt, handleChange])

  // Forward wheel events from wrapper to textarea
  useEffect(() => {
    // Use wrapScrollRef if provided (for TextBoxWrapperOneLine), otherwise use local container
    const wrapperElement = wrapScrollRef?.current || containerRef.current;
    const textareaElement = textareaRef.current;

    if (!wrapperElement || !textareaElement) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if the textarea is scrollable
      const isScrollable = textareaElement.scrollHeight > textareaElement.clientHeight;
      
      if (isScrollable) {
        e.preventDefault();
        e.stopPropagation();
        textareaElement.scrollTop += e.deltaY;
      }
    };

    wrapperElement.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      wrapperElement.removeEventListener('wheel', handleWheel);
    };
  }, [wrapScrollRef]);

  const shouldShowAnimatedPlaceholder = Array.isArray(placeholder)

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {prompt && showClearTextButton ? (
        <div
          className={css({
            position: 'absolute',
            top: isCollapsed ? '-1px' : '0',
            right: isCollapsed ? '0' : '12px',
            zIndex: 1,
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            cursor: 'pointer',
            background: isCollapsed ? lightTheme.colors.white : lightTheme.colors.grayScale50,
            ':hover': {
              background: isCollapsed ? lightTheme.colors.white : lightTheme.colors.grayScale100,
            },
            ':active': {
              background: isCollapsed ? lightTheme.colors.white : lightTheme.colors.grayScale150,
            },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          })}
          onClick={() => {
            if (!isCollapsed) {
              // Revert UX: clear immediately and recompute height like before
              if (textareaRef.current) {
                textareaRef.current.value = ''
                handleChange({ target: textareaRef.current })
              } else {
                setPrompt('')
              }
            } else {
              wrapScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
              setTimeout(() => {
                textareaRef.current?.focus()
                setIsCollapsed(false)
              }, 100)
            }
          }}
        >
          {isCollapsed ? <Icons.CollapseIcon /> : <Icons.Close size={20} />}
        </div>
      ) : null}
      <input
        style={{
          position: 'relative',
          width: '220px',
          height: '20px',
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: lightTheme.colors.blackGray,
          overflow: 'hidden!important',
          whiteSpace: 'wrap',
          textOverflow: 'ellipsis',
          cursor: 'pointer',
          opacity: isCollapsed ? 1 : 0,
          display: isCollapsed ? 'block' : 'none',
          transition: 'opacity 0.3s',
        }}
        value={prompt}
        readOnly
        onClick={(e) => {
          e.stopPropagation()
          wrapScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
          setTimeout(() => {
            setIsCollapsed(false)
          }, 100)
        }}
      />
      {shouldShowAnimatedPlaceholder && 
        <AnimatedPlaceholder 
          placeholders={placeholder} 
          disabled={disabled} 
          visible={!prompt} 
          hasValue={!!prompt} 
          isFocused={isFocused}
        />
      }
      <textarea
        id={textareaId}
        ref={textareaRef}
        className={css({
          position: 'relative',
          width: '100%',
          minHeight: `20px`,
          maxHeight: `${6 * 20}px`,
          border: 'none',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '20px',
          color: lightTheme.colors.blackGray,
          letterSpacing: '-0.084px',
          padding: padding ? padding.map(p => `${p}px`).join(' ') : `0 ${!showClearTextButton ? '0' : prompt ? '40px' : 0} 0 0`,
          resize: 'none',
          '::-webkit-scrollbar': {
            width: '8px',
          },
          '::-webkit-scrollbar-track': {
            marginTop: `${scrollbarTrackMarginTop}px`,
          },
          '::-webkit-scrollbar-thumb': {
            background: lightTheme.colors.grayScale100,
            borderRadius: '10px',
          },
          whiteSpace: 'wrap',
          textOverflow: 'ellipsis',
          cursor: 'auto',
          opacity: isCollapsed ? 0 : 1,
          display: isCollapsed ? 'none' : 'block',
          transition: 'opacity 0.3s',
          height: 'auto',
          fieldSizing: 'content' as any, // Modern CSS property for auto-sizing
          WebkitFieldSizing: 'content' as any,
          MozFieldSizing: 'content' as any,
          '::placeholder': {
            color: disabled
              ? lightTheme.colors.text.text_200_disable
              : lightTheme.colors.text.text_600_default,
            opacity: Array.isArray(placeholder) ? 0 : 1,
          },
        })}
        placeholder={!Array.isArray(placeholder) ? t(placeholder) : ''}
        value={prompt}
        onChange={handleChange}
        onKeyDown={e => {
          // @ts-ignore
          const isUndoRedoShortcut = shourcutsManager.isCtrlZ(e) || shourcutsManager.isCtrlShiftZ(e) || shourcutsManager.isCtrlY(e)
          if(isUndoRedoShortcut) {
            e.preventDefault()
            onUndoRedoShortcut?.()
          } else {
            e.stopPropagation()
          }
          handleKeyDown(e)
        }}
        rows={1}
        onFocus={() => {
          setIsFocused(true)
          onFocus?.()
        }}
        onBlur={(e) => {
          if(!onBlur) {
            e.persist()
            return
          }
          
          setIsFocused(false)
          onBlur?.()
        }}
      ></textarea>
    </div>
  );
};

export default React.memo(DynamicTextarea);